import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { medicalSchools, matchSchools } from '../data/schools';
import { supabase } from '../lib/supabase';
import { loadStats, getCachedStats } from '../lib/userStats';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const CREAM = '#f8fafc';
const CREAM_DARK = '#f1f5f9';
const CREAM_BORDER = '#e5e7eb';
const SAGE = '#dbeafe';
const EMERALD = '#3b82f6';
const EMERALD_DARK = '#1e40af';
const TEXT_DARK = '#0f172a';
const TEXT_MID = '#374151';
const TEXT_MUTED = '#64748b';

function Card({ children, style = {} }) {
  return (
    <div style={{
      background: CREAM,
      border: '0.5px solid ' + CREAM_BORDER,
      borderRadius: 14,
      padding: '1.25rem 1.5rem',
      ...style
    }}>
      {children}
    </div>
  );
}

function SectionTitle({ children, sub }) {
  return (
    <div style={{ marginBottom: '1.1rem' }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: TEXT_DARK }}>{children}</div>
      {sub && <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function WickBar({ label, dist, userVal, decimals = 0 }) {
  if (!dist || !dist.median) return null;

  var safeP10 = dist.p10 != null ? dist.p10 : (decimals > 0 ? dist.median - 0.4 : dist.median - 4);
  var safeP25 = dist.p25 != null ? dist.p25 : (decimals > 0 ? dist.median - 0.15 : dist.median - 2);
  var safeP75 = dist.p75 != null ? dist.p75 : (decimals > 0 ? dist.median + 0.15 : dist.median + 2);
  var safeP90 = dist.p90 != null ? dist.p90 : (decimals > 0 ? dist.median + 0.4 : dist.median + 4);

  var min = decimals > 0 ? safeP10 - 0.1 : safeP10 - 1;
  var max = decimals > 0 ? safeP90 + 0.1 : safeP90 + 1;
  var range = max - min;

  var pct = function(v) { return Math.max(2, Math.min(96, ((v - min) / range) * 100)); };
  var fmt = function(v) { return v == null ? '--' : decimals > 0 ? (+v).toFixed(decimals) : v; };

  var verdictText = '--';
  var verdictColor = TEXT_MUTED;

  if (userVal != null) {
    var uv = +userVal;
    if (uv >= (safeP75 || dist.median)) {
      verdictText = fmt(uv) + ' -- above most admitted students';
      verdictColor = '#047857';
    } else if (uv >= dist.median) {
      verdictText = fmt(uv) + ' -- near the median';
      verdictColor = '#b45309';
    } else {
      verdictText = fmt(uv) + ' -- below most admitted students';
      verdictColor = '#b91c1c';
    }
  }

  return (
    <div style={{ marginBottom: '1.4rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_DARK }}>{label}</span>
        {userVal && <span style={{ fontSize: 11, fontWeight: 600, color: verdictColor }}>{verdictText}</span>}
      </div>
      <div style={{ position: 'relative', height: 26, display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, height: 8, background: SAGE, borderRadius: 4 }} />
        <div style={{ position: 'absolute', left: pct(safeP25) + '%', width: (pct(safeP75) - pct(safeP25)) + '%', height: 8, background: EMERALD, borderRadius: 4 }} />
        <div style={{ position: 'absolute', left: pct(dist.median) + '%', transform: 'translateX(-50%)', width: 2, height: 22, background: EMERALD_DARK, borderRadius: 1 }} />
        {userVal && (
          <div style={{ position: 'absolute', left: pct(+userVal) + '%', transform: 'translateX(-50%)', width: 20, height: 20, background: CREAM, border: '3px solid #1d4ed8', borderRadius: '50%', zIndex: 2 }} />
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: TEXT_MUTED, marginTop: 5 }}>
        <span>{fmt(safeP10)}</span>
        <span>Most: {fmt(safeP25)}-{fmt(safeP75)}</span>
        <span style={{ color: EMERALD_DARK, fontWeight: 600 }}>{fmt(dist.median)} median</span>
        <span>{fmt(safeP90)}</span>
      </div>
    </div>
  );
}

function FunnelRow({ label, total, maxTotal, color }) {
  var width = maxTotal > 0 ? Math.max(0.5, (total / maxTotal) * 100) : 0;
  var pct = maxTotal > 0 ? ((total / maxTotal) * 100).toFixed(1) : '--';
  var isBase = label === 'Applied';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
      <div style={{ width: 90, fontSize: 13, fontWeight: 600, color: TEXT_DARK, flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 10, background: SAGE, borderRadius: 5, overflow: 'hidden' }}>
        <div style={{ width: width + '%', height: '100%', background: color, borderRadius: 5 }} />
      </div>
      <div style={{ width: 60, textAlign: 'right', fontSize: 13, fontWeight: 700, color: TEXT_DARK, flexShrink: 0 }}>
        {isBase ? '' : pct + '%'}
      </div>
    </div>
  );
}

function StatRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '0.5px solid ' + CREAM_BORDER }}>
      <span style={{ fontSize: 12, color: TEXT_MUTED }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_DARK }}>{value}</span>
    </div>
  );
}

function StatCluster({ title, children }) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em', color: TEXT_MUTED, marginBottom: 8, paddingBottom: 6, borderBottom: '0.5px solid ' + CREAM_BORDER }}>{title}</div>
      {children}
    </div>
  );
}

function DeadlinePill({ label, date }) {
  var isNA = !date || date === 'N/A';
  return (
    <div style={{ flex: 1, background: isNA ? CREAM_DARK : '#eff6ff', border: '0.5px solid ' + (isNA ? CREAM_BORDER : '#bfdbfe'), borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: isNA ? TEXT_MUTED : EMERALD, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: isNA ? TEXT_MUTED : TEXT_DARK }}>{isNA ? 'Rolling' : date}</div>
    </div>
  );
}

export default function SchoolDetail({ session }) {
  const isMobile = useIsMobile();
  var params = useParams();
  var id = params.id;
  var navigate = useNavigate();
  var [saved, setSaved] = useState(false);
  var [checklist, setChecklist] = useState({});
  var [checklistError, setChecklistError] = useState(false);
  var [userStatsData, setUserStatsData] = useState(function() {
    var uid = session && session.user ? session.user.id : null;
    return uid ? getCachedStats(uid) : {};
  });

  var school = medicalSchools.find(function(s) { return s.id === id; });

  var userMCAT = userStatsData.mcat ? +userStatsData.mcat : null;
  var userGPA = userStatsData.gpa ? +userStatsData.gpa : null;
  var userSciGPA = userStatsData.sciGpa ? +userStatsData.sciGpa : null;
  var hasStats = userMCAT && userGPA && userStatsData.state;

  useEffect(function() {
    if (!session) return;
    loadStats(session.user.id).then(function(s) { setUserStatsData(s); });
  }, [session]);

  useEffect(function() {
    if (!session || !school) return;
    supabase.from('saved_schools').select('school_id').eq('user_id', session.user.id).eq('school_id', id)
      .then(function({ data, error }) {
        if (!error && data) setSaved(data.length > 0);
      });
  }, [id, session, school]);

  useEffect(function() {
    if (!session || !school) return;
    supabase.from('checklists').select('*')
      .eq('user_id', session.user.id)
      .eq('school_id', id)
      .then(function({ data, error }) {
        if (!error && data) {
          var map = {};
          data.forEach(function(r) { map[r.step] = { done: r.done, rowId: r.id }; });
          setChecklist(map);
        }
      });
  }, [id, session, school]);

  useEffect(function() {
    if (!checklistError) return;
    var t = setTimeout(function() { setChecklistError(false); }, 3000);
    return function() { clearTimeout(t); };
  }, [checklistError]);

  // eslint-disable-next-line no-unused-vars
  async function toggleStep(step) {
    if (!session) return;
    var current = checklist[step];
    var newDone = !current?.done;
    setChecklist(function(prev) { return Object.assign({}, prev, { [step]: Object.assign({}, prev[step], { done: newDone }) }); });
    if (current?.rowId) {
      var updateResult = await supabase.from('checklists').update({ done: newDone }).eq('id', current.rowId);
      if (updateResult.error) {
        setChecklist(function(prev) { return Object.assign({}, prev, { [step]: current }); });
        setChecklistError(true);
      }
    } else {
      var result = await supabase.from('checklists').insert({
        user_id: session.user.id, school_id: id, step: step, done: newDone
      }).select().single();
      if (result.data) {
        setChecklist(function(prev) { return Object.assign({}, prev, { [step]: { done: newDone, rowId: result.data.id } }); });
      } else {
        setChecklist(function(prev) { return Object.assign({}, prev, { [step]: current }); });
        setChecklistError(true);
      }
    }
  }

  // eslint-disable-next-line no-unused-vars
  async function setOutcome(outcome) {
    if (!session) return;
    var OUTCOMES = ['Accepted', 'Waitlisted', 'Rejected', 'Withdrawn'];
    OUTCOMES.forEach(async function(o) {
      var current = checklist[o];
      var newDone = o === outcome ? !current?.done : false;
      setChecklist(function(prev) { return Object.assign({}, prev, { [o]: Object.assign({}, prev[o], { done: newDone }) }); });
      if (current?.rowId) {
        var updateResult = await supabase.from('checklists').update({ done: newDone }).eq('id', current.rowId);
        if (updateResult.error) {
          setChecklist(function(prev) { return Object.assign({}, prev, { [o]: current }); });
          setChecklistError(true);
        }
      } else if (newDone) {
        var result = await supabase.from('checklists').insert({
          user_id: session.user.id, school_id: id, step: o, done: true
        }).select().single();
        if (result.data) {
          setChecklist(function(prev) { return Object.assign({}, prev, { [o]: { done: true, rowId: result.data.id } }); });
        } else {
          setChecklist(function(prev) { return Object.assign({}, prev, { [o]: current }); });
          setChecklistError(true);
        }
      }
    });
  }

  async function toggleSave() {
    if (!session) return;
    if (saved) {
      setSaved(false);
      const { error } = await supabase.from('saved_schools').delete().eq('user_id', session.user.id).eq('school_id', id);
      if (error) setSaved(true);
    } else {
      setSaved(true);
      const { error } = await supabase.from('saved_schools').insert({ user_id: session.user.id, school_id: id, school_name: school.name, category: null });
      if (error) setSaved(false);
    }
  }

  if (!school) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, background: CREAM, minHeight: '100vh' }}>
      <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', color: TEXT_DARK }}>School not found</div>
      <button onClick={function() { navigate('/dashboard'); }} style={{ padding: '8px 20px', background: EMERALD, color: CREAM, border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Back to Dashboard</button>
    </div>
  );

  if (!school.mcat || !school.mcat.median) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, background: CREAM, minHeight: '100vh' }}>
      <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', color: TEXT_DARK }}>Data coming soon</div>
      <button onClick={function() { navigate(-1); }} style={{ padding: '8px 20px', background: EMERALD, color: CREAM, border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Go Back</button>
    </div>
  );

  var stats = school.applicationStats;
  var totalApps = stats?.verifiedApps?.total || 0;
  var totalInterviewed = stats?.interviewed?.total || 0;
  var totalMatriculated = stats?.matriculated?.total || 0;
  var acceptanceRate = totalApps > 0 && totalMatriculated > 0 ? ((totalMatriculated / totalApps) * 100).toFixed(1) + '%' : null;
  var interviewRate = totalApps > 0 && totalInterviewed > 0 ? ((totalInterviewed / totalApps) * 100).toFixed(1) + '%' : null;
  var oosMat = stats?.matriculated?.outOfState || 0;
  var oosPctOfClass = totalMatriculated > 0 ? ((oosMat / totalMatriculated) * 100).toFixed(0) + '%' : null;
  var isMat = stats?.matriculated?.inState || 0;
  var isPctOfClass = totalMatriculated > 0 ? ((isMat / totalMatriculated) * 100).toFixed(0) + '%' : null;
  var intlMat = stats?.matriculated?.international || 0;
  var intlPctOfClass = totalMatriculated > 0 && intlMat > 0 ? ((intlMat / totalMatriculated) * 100).toFixed(0) + '%' : null;

  // Verdict — driven exclusively by matchSchools, same as Dashboard
  var matchResult = null;
  if (hasStats) {
    var filters = (function() {
      try {
        var uid = session && session.user ? session.user.id : 'guest';
        return JSON.parse(localStorage.getItem('previsr_filters_' + uid) || '{"degreeTypes":["MD","DO"],"regions":["US","Canada","Caribbean","Puerto Rico"]}');
      } catch (e) { return { degreeTypes: ['MD', 'DO'], regions: ['US', 'Canada', 'Caribbean', 'Puerto Rico'] }; }
    })();
    var allResults = matchSchools({
      mcat: userMCAT, gpa: userGPA, sciGpa: userSciGPA,
      state: userStatsData.state,
      degreeTypes: filters.degreeTypes && filters.degreeTypes.length ? filters.degreeTypes : ['MD', 'DO'],
      regions: filters.regions && filters.regions.length ? filters.regions : ['US', 'Canada', 'Caribbean', 'Puerto Rico'],
    });
    matchResult = allResults.find(function(r) { return r.id === id; }) || null;
  }

  var VERDICT_STYLES = {
    safety:   { bg: '#eff6ff', border: '#bfdbfe', textColor: '#1e40af', title: "You're a strong candidate here",  body: "Your stats place you above most admitted students. This school belongs on your list.", label: 'Target' },
    match:    { bg: '#eef2ff', border: '#c7d2fe', textColor: '#4338ca', title: "You're right in the mix",         body: "Your stats are close to what admitted students had. A strong application can get you here.", label: 'Match' },
    reach:    { bg: '#fffbeb', border: '#fde68a', textColor: '#92400e', title: "This is a reach for you",         body: "Your stats are below most admitted students. You can still apply -- go in with realistic expectations.", label: 'Reach' },
    longshot: { bg: '#f8fafc', border: '#e2e8f0', textColor: '#475569', title: "This is a long shot",             body: "Your stats are significantly below this school's admitted class. Consider this only if it's your top choice.", label: 'Long Shot' },
  };
  var verdict = matchResult ? VERDICT_STYLES[matchResult.category] : null;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', background: CREAM_DARK }}>

      {/* Header */}
      <div style={{ background: CREAM, borderBottom: '0.5px solid ' + CREAM_BORDER, padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: 14, position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={function() { navigate(-1); }} style={{ background: CREAM_DARK, border: '0.5px solid ' + CREAM_BORDER, borderRadius: 8, padding: '7px 14px', color: TEXT_MID, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><polyline points="15,18 9,12 15,6"/></svg>
          Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? 15 : 20, fontWeight: 700, color: TEXT_DARK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{school.name}</h1>
          <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 2 }}>{school.city}, {school.state} -- {school.type} -- {school.region}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={toggleSave} style={{ padding: '7px 16px', borderRadius: 8, border: '0.5px solid ' + (saved ? EMERALD : CREAM_BORDER), background: saved ? '#eff6ff' : CREAM_DARK, color: saved ? EMERALD : TEXT_MID, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="13" height="13" fill={saved ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
            {saved ? 'Saved' : 'Save School'}
          </button>
        </div>
      </div>

      <div style={{ padding: isMobile ? '1rem' : '1.5rem', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Verdict banner */}
        {!hasStats && (
          <div style={{ background: CREAM, border: '0.5px solid ' + CREAM_BORDER, borderRadius: 14, padding: '1.25rem 1.5rem' }}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em', color: TEXT_MUTED, marginBottom: 5 }}>Your fit</div>
            <div style={{ fontSize: 13, color: TEXT_MID }}>Enter your stats on the Dashboard to see your fit at this school.</div>
          </div>
        )}
        {verdict && (
          <div style={{ background: verdict.bg, border: '0.5px solid ' + verdict.border, borderRadius: 14, padding: '1.25rem 1.5rem', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: isMobile ? 12 : 0 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em', color: verdict.textColor, marginBottom: 5 }}>Your fit</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: verdict.textColor, marginBottom: 4 }}>{verdict.title}</div>
              <div style={{ fontSize: 12, color: verdict.textColor, opacity: .8 }}>{verdict.body}</div>
              {matchResult.mcatPercentile != null && (
                <div style={{ fontSize: 11, color: verdict.textColor, opacity: .65, marginTop: 6 }}>
                  MCAT {matchResult.mcatPercentile}th pct -- GPA {matchResult.gpaPercentile}th pct at this school
                </div>
              )}
            </div>
            <div style={{ background: CREAM, borderRadius: 10, padding: '12px 24px', textAlign: 'center', flexShrink: 0, marginLeft: isMobile ? 0 : 24, border: '0.5px solid ' + verdict.border }}>
              <div style={{ fontSize: 9, color: verdict.textColor, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.08em' }}>Match level</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: verdict.textColor }}>{verdict.label}</div>
            </div>
          </div>
        )}

        {/* Application Checklist */}
        <Card style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: TEXT_DARK }}>Application checklist</span>
            <span style={{ fontSize: 10, color: TEXT_MUTED }}>Track your progress at this school</span>
          </div>
          {checklistError && (
            <div style={{ fontSize: 11, color: '#b91c1c', background: '#fef2f2', border: '0.5px solid #fecaca', borderRadius: 6, padding: '5px 8px', marginBottom: 6 }}>
              Couldn't save that change. Please try again.
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {['Primary submitted', 'Secondary requested', 'Secondary submitted', 'Interview invite', 'Interview scheduled', 'Interview complete'].map(function(step) {
              var done = checklist[step]?.done;
              return (
                <div key={step} onClick={function() { toggleStep(step); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', borderRadius: 4, cursor: 'pointer', transition: 'background .1s' }}>
                  <div style={{ width: 14, height: 14, borderRadius: 3, background: done ? EMERALD : 'transparent', border: '1.5px solid ' + (done ? EMERALD : CREAM_BORDER), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {done && <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <span style={{ fontSize: 12, color: done ? TEXT_MUTED : TEXT_MID, flex: 1 }}>{step}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: EMERALD, visibility: done ? 'visible' : 'hidden', flexShrink: 0 }}>Done</span>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '0.5px solid ' + CREAM_BORDER, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '.08em', flexShrink: 0 }}>Decision</span>
            {[
              { label: 'Accepted',   bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af' },
              { label: 'Waitlisted', bg: '#fffbeb', border: '#fde68a', color: '#92400e' },
              { label: 'Rejected',   bg: '#fef2f2', border: '#fecaca', color: '#b91c1c' },
              { label: 'Withdrawn',  bg: CREAM_DARK, border: CREAM_BORDER, color: TEXT_MUTED },
            ].map(function(item) {
              var selected = checklist[item.label]?.done;
              return (
                <div key={item.label} onClick={function() { setOutcome(item.label); }}
                  style={{ padding: '2px 10px', borderRadius: 10, border: '0.5px solid ' + (selected ? item.border : CREAM_BORDER), background: selected ? item.bg : 'transparent', color: selected ? item.color : TEXT_MUTED, fontSize: 11, fontWeight: selected ? 700 : 400, cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap' }}>
                  {item.label}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Deadlines */}
        <Card>
          <SectionTitle>Application Deadlines</SectionTitle>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
            <DeadlinePill label="Primary (AMCAS opens 5/28)" date={school.primaryDeadline} />
            <DeadlinePill label="Secondary Deadline" date={school.secondaryDeadline} />
          </div>
          <div style={{ marginTop: 12, fontSize: 11, color: TEXT_MUTED, lineHeight: 1.6 }}>
            AMCAS opens for data entry May 5 and submission May 28 each cycle. Submit your primary as early as possible -- schools review on a rolling basis and earlier submissions have a meaningful advantage.
          </div>
        </Card>

        {/* Body: left column + right sidebar */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 280px', gap: 14 }}>

          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Wick bars */}
            <Card>
              <SectionTitle sub="Based on the most recent entering class data">How your stats compare</SectionTitle>
              <WickBar label="MCAT" dist={school.mcat} userVal={userMCAT} decimals={0} />
              <WickBar label="GPA" dist={school.gpa} userVal={userGPA} decimals={2} />
              {school.sciGpa && (
                <div style={{ marginBottom: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_DARK }}>Science GPA</span>
                    {userSciGPA && <span style={{ fontSize: 11, fontWeight: 600, color: +userSciGPA >= school.sciGpa ? '#047857' : '#b91c1c' }}>{(+userSciGPA).toFixed(2)} -- {+userSciGPA >= school.sciGpa ? 'above' : 'below'} school average</span>}
                  </div>
                  <div style={{ position: 'relative', height: 26, display: 'flex', alignItems: 'center' }}>
                    <div style={{ position: 'absolute', left: 0, right: 0, height: 8, background: SAGE, borderRadius: 4 }} />
                    <div style={{ position: 'absolute', left: '49%', transform: 'translateX(-50%)', width: 2, height: 22, background: EMERALD_DARK, borderRadius: 1 }} />
                    {userSciGPA && (
                      <div style={{ position: 'absolute', left: Math.max(5, Math.min(93, ((+userSciGPA - (school.sciGpa - 0.5)) / 1.0) * 100)) + '%', transform: 'translateX(-50%)', width: 20, height: 20, background: CREAM, border: '3px solid #1d4ed8', borderRadius: '50%', zIndex: 2 }} />
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: TEXT_MUTED, marginTop: 5 }}>
                    <span>{(school.sciGpa - 0.5).toFixed(2)}</span>
                    <span style={{ color: EMERALD_DARK, fontWeight: 600 }}>{school.sciGpa} avg</span>
                    <span>{(school.sciGpa + 0.5).toFixed(2)}</span>
                  </div>
                </div>
              )}
              {/* Legend */}
              <div style={{ display: 'flex', gap: 16, paddingTop: 14, marginTop: 14, borderTop: '0.5px solid ' + CREAM_BORDER, flexWrap: 'wrap' }}>
                {[
                  [SAGE, 8, 'Full range'],
                  [EMERALD, 8, 'Most students'],
                  [null, null, 'Median', EMERALD_DARK],
                ].map(function(p) {
                  return (
                    <span key={p[2]} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: TEXT_MUTED }}>
                      {p[3] ? <span style={{ width: 2, height: 13, background: p[3], display: 'inline-block', borderRadius: 1 }} /> : <span style={{ width: 18, height: p[1], background: p[0], borderRadius: 2, display: 'inline-block' }} />}
                      {p[2]}
                    </span>
                  );
                })}
                {[['#047857', 'Above median'], ['#b45309', 'Near median'], ['#b91c1c', 'Below median'], ['#1d4ed8', 'You']].map(function(p) {
                  return (
                    <span key={p[1]} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: p[0] }}>
                      <span style={{ width: 12, height: 12, background: CREAM, border: '2px solid ' + p[0], borderRadius: '50%', display: 'inline-block' }} />
                      {p[1]}
                    </span>
                  );
                })}
              </div>
            </Card>

            {/* Funnel */}
            {totalApps > 0 && (
              <Card>
                <SectionTitle sub="How many applicants make it through each stage">Getting in</SectionTitle>
                <FunnelRow label="Applied" total={totalApps} maxTotal={totalApps} color={SAGE} />
                {totalInterviewed > 0 && <FunnelRow label="Interviewed" total={totalInterviewed} maxTotal={totalApps} color={EMERALD} />}
                {totalMatriculated > 0 && <FunnelRow label="Enrolled" total={totalMatriculated} maxTotal={totalApps} color={EMERALD_DARK} />}
              </Card>
            )}

            {/* Mission statement */}
            {school.mission && (
              <Card>
                <SectionTitle>Mission Statement</SectionTitle>
                <p style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.8, margin: 0 }}>{school.mission}</p>
              </Card>
            )}


          </div>

          {/* Right sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* At a glance */}
            <Card>
              <SectionTitle>At a Glance</SectionTitle>
              <StatCluster title="MCAT">
                <StatRow label="Median" value={school.mcat?.median} />
                <StatRow label="Most students" value={school.mcat?.p25 && school.mcat?.p75 ? school.mcat.p25 + '-' + school.mcat.p75 : null} />
                <StatRow label="10th-90th pct" value={school.mcat?.p10 && school.mcat?.p90 ? school.mcat.p10 + '-' + school.mcat.p90 : null} />
              </StatCluster>
              <StatCluster title="GPA">
                <StatRow label="Overall median" value={school.gpa?.median ? school.gpa.median.toFixed(2) : null} />
                <StatRow label="Science GPA avg" value={school.sciGpa ? school.sciGpa.toFixed(2) : null} />
              </StatCluster>
              <StatCluster title="Admissions">
                <StatRow label="Acceptance rate" value={acceptanceRate} />
                <StatRow label="Interview rate" value={interviewRate} />
                <StatRow label="Total applicants" value={totalApps > 0 ? totalApps.toLocaleString() : null} />
                <StatRow label="Class size" value={totalMatriculated > 0 ? totalMatriculated.toLocaleString() : null} />
              </StatCluster>
              <StatCluster title="Class Composition">
                <StatRow label="In-state" value={isPctOfClass} />
                <StatRow label="Out-of-state" value={oosPctOfClass} />
                {intlPctOfClass && <StatRow label="International" value={intlPctOfClass} />}
              </StatCluster>
            </Card>

            {/* Deadlines compact repeat */}
            <Card>
              <SectionTitle>Key Dates</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid ' + CREAM_BORDER }}>
                  <span style={{ fontSize: 12, color: TEXT_MUTED }}>AMCAS opens</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: TEXT_DARK }}>05/28/2026</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid ' + CREAM_BORDER }}>
                  <span style={{ fontSize: 12, color: TEXT_MUTED }}>Primary deadline</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: TEXT_DARK }}>{school.primaryDeadline || 'Rolling'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                  <span style={{ fontSize: 12, color: TEXT_MUTED }}>Secondary deadline</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: TEXT_DARK }}>{(!school.secondaryDeadline || school.secondaryDeadline === 'N/A') ? 'Rolling' : school.secondaryDeadline}</span>
                </div>
              </div>
              <div style={{ marginTop: 10, padding: '10px 12px', background: '#fffbeb', border: '0.5px solid #fde68a', borderRadius: 8, fontSize: 11, color: '#92400e', lineHeight: 1.6 }}>
                Submit each secondary within 2 weeks of requesting it. Schools track response time.
              </div>
            </Card>

          </div>
        </div>
      </div>
    </div>
  );
}
